import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatMoney, todayISO } from "../lib/api";
import { toast } from "sonner";
import { Save, Flame, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function BulkPurchaseEntry() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [vendors, setVendors] = useState([]);
  const [bullets, setBullets] = useState([]);
  const [stockMap, setStockMap] = useState({}); // bullet name -> kg
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    date: todayISO(),
    challan_no: "",
    party_id: "",
    bullet: "",
    kg: 0,
    rate_per_kg: 0,
    vehicle_no: "",
    driver_name: "",
    notes: "",
  });

  const load = async () => {
    try {
      const [p, g, s] = await Promise.all([
        api.get("/parties?type=vendor"),
        api.get("/godowns"),
        api.get("/stock/bullet"),
      ]);
      setVendors(p.data);
      const bs = (g.data || []).filter((x) => x.kind === "bullet");
      setBullets(bs);
      setStockMap(s.data || {});
      setForm((f) => ({ ...f, bullet: f.bullet || bs[0]?.name || "" }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const amount = useMemo(
    () => Math.round(Number(form.kg || 0) * Number(form.rate_per_kg || 0) * 100) / 100,
    [form.kg, form.rate_per_kg]
  );

  if (isManager) {
    return (
      <div className="card-flat p-8 text-center">
        <AlertTriangle className="mx-auto text-gray-400 mb-3" size={32} />
        <div className="font-heading font-bold text-xl">Restricted</div>
        <p className="text-sm text-gray-600 mt-2">Bulk purchases are recorded by the owner only.</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.party_id) { toast.error("Vendor required"); return; }
    if (!form.bullet) { toast.error("Bullet godown required"); return; }
    if (Number(form.kg) <= 0) { toast.error("KG must be > 0"); return; }
    if (Number(form.rate_per_kg) <= 0) { toast.error("Rate per kg must be > 0"); return; }
    setBusy(true);
    try {
      await api.post("/transactions", {
        type: "bulk_purchase",
        date: form.date,
        challan_no: form.challan_no || "",
        party_id: form.party_id,
        to_godown: form.bullet,
        bullet_godown: form.bullet,
        kg: Number(form.kg),
        rate_per_kg: Number(form.rate_per_kg),
        total_amount: amount,
        vehicle_no: form.vehicle_no || "",
        driver_name: form.driver_name || "",
        notes: form.notes || "",
        items: [],
      });
      toast.success(`Bulk purchase saved · ${form.kg} KG added to ${form.bullet}`);
      setForm({
        date: todayISO(), challan_no: "", party_id: form.party_id,
        bullet: form.bullet, kg: 0, rate_per_kg: 0,
        vehicle_no: "", driver_name: "", notes: "",
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="entry-form-bulk-purchase">
      <p className="small-label text-gray-500">
        Bulk Purchase · vendor delivers LPG in <span className="font-bold">KG</span> directly into a bullet tank. No cylinders involved here.
      </p>

      {bullets.length === 0 && (
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A] flex items-start gap-3">
          <AlertTriangle className="text-[#B45309] flex-shrink-0" size={20} />
          <div className="text-sm">
            <div className="font-bold text-[#B45309]">No Bullet godowns yet</div>
            <div className="text-[#92400E]">Create one in Masters → Godowns and set kind = <span className="font-bold">Bullet</span>.</div>
          </div>
        </div>
      )}

      {/* Bullet stock cards */}
      {bullets.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {bullets.map((b) => (
            <div key={b.id} className="card-flat p-4 bg-[#FFF7ED] border-[#FED7AA]">
              <div className="flex items-center gap-1.5 small-label text-[#B45309]">
                <Flame size={12} /> {b.name}
              </div>
              <div className="data-num text-2xl font-bold mt-1 text-[#B45309]">
                {formatMoney(Math.round((stockMap[b.name] || 0) * 100) / 100)} <span className="text-base font-normal text-gray-600">KG</span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">current bullet stock</div>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="small-label block mb-2">Date</label>
          <input type="date" className="input-lg" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="bulk-date" required />
        </div>
        <div>
          <label className="small-label block mb-2">Challan / Invoice No</label>
          <input className="input-lg" value={form.challan_no}
            onChange={(e) => setForm({ ...form, challan_no: e.target.value })}
            placeholder="vendor's invoice no"
            data-testid="bulk-challan" />
        </div>
        <div>
          <label className="small-label block mb-2">Vendor</label>
          <select className="input-lg" value={form.party_id}
            onChange={(e) => setForm({ ...form, party_id: e.target.value })}
            required data-testid="bulk-vendor">
            <option value="">— Select vendor —</option>
            {vendors.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="small-label block mb-2">Bullet Godown</label>
          <select className="input-lg" value={form.bullet}
            onChange={(e) => setForm({ ...form, bullet: e.target.value })}
            required data-testid="bulk-godown">
            <option value="">— Select bullet —</option>
            {bullets.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        </div>
      </div>

      {/* KG + Rate */}
      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="small-label block mb-2">KG loaded</label>
          <input type="number" step="0.01" min="0" className="input-lg text-right data-num text-xl font-bold"
            value={form.kg} onChange={(e) => setForm({ ...form, kg: e.target.value })}
            data-testid="bulk-kg" required />
        </div>
        <div>
          <label className="small-label block mb-2">Rate ₹ / KG</label>
          <input type="number" step="0.01" min="0" className="input-lg text-right data-num text-xl font-bold"
            value={form.rate_per_kg} onChange={(e) => setForm({ ...form, rate_per_kg: e.target.value })}
            data-testid="bulk-rate" required />
        </div>
        <div>
          <label className="small-label block mb-2">Total ₹ (auto)</label>
          <div className="input-lg flex items-center justify-end data-num text-xl font-black bg-gray-50" data-testid="bulk-total">
            ₹ {formatMoney(amount)}
          </div>
        </div>
      </div>

      {/* Vehicle / notes */}
      <div className="card-flat p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="small-label block mb-2">Vehicle</label>
          <input className="input-lg" value={form.vehicle_no}
            onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })}
            placeholder="tanker no" data-testid="bulk-vehicle" />
        </div>
        <div>
          <label className="small-label block mb-2">Driver</label>
          <input className="input-lg" value={form.driver_name}
            onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
            data-testid="bulk-driver" />
        </div>
        <div>
          <label className="small-label block mb-2">Notes</label>
          <input className="input-lg" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="bulk-notes" />
        </div>
      </div>

      <div className="flex justify-end">
        <button disabled={busy || bullets.length === 0} className="btn-primary h-12 px-8 flex items-center gap-2" data-testid="bulk-save">
          <Save size={16} /> {busy ? "Saving…" : "Save Bulk Purchase"}
        </button>
      </div>
    </form>
  );
}
